import React, { useMemo } from 'react';
import { Dialog } from '../reusable';
import ProductionStatusChip from './ProductionStatusChip';
import {
  FORMULA_KIND_COLOR,
  formulaLineCostBreakdown,
  formulaMassPercentages,
  getFormulaStatusColor,
  formatDateEs,
  formatQty,
  formulaKindLabel,
  normalizeFormulaKind,
} from './productionDomain';
import { formatMoneyClp } from './rawMaterialsDomain';

const Field = ({ label, value, muted }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 600, color: muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
    <div style={{ marginTop: 2, fontSize: 14 }}>{value || '—'}</div>
  </div>
);

const PaintFormulaDetailModal = ({
  isOpen,
  onClose,
  formula,
  isDarkMode = false,
  ui,
  materials = [],
  onEdit,
}) => {
  const materialByCodeMap = useMemo(() => {
    const map = {};
    (materials || []).forEach((row) => {
      const code = String(row.code || '').trim();
      if (code) map[code] = row;
    });
    return map;
  }, [materials]);

  if (!formula) return null;
  const lines = Array.isArray(formula.lines) ? formula.lines : [];
  const techParams = Array.isArray(formula.tech_params) ? formula.tech_params : [];
  const massPercents = formulaMassPercentages(lines, materialByCodeMap);
  const costBasis = String(formula.cost_basis || 'L') === 'gl' ? 'gl' : 'L';
  const muted = ui?.muted || (isDarkMode ? '#a1a1aa' : '#64748b');
  const text = ui?.text || (isDarkMode ? '#f4f4f5' : '#0f172a');
  const border = ui?.border || (isDarkMode ? '#3f3f46' : '#e2e8f0');

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={formula.code || 'Fórmula'}
      subtitle={`${formulaKindLabel(normalizeFormulaKind(formula))} · ${
        formula.name || formula.product_name || 'Sin nombre'
      }`}
      size="lg"
      isDarkMode={isDarkMode}
    >
      <div style={{ padding: '0 20px 20px', color: text }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 18 }}>
          <Field label="Tipo" value={formulaKindLabel(normalizeFormulaKind(formula))} muted={muted} />
          <Field label="Nombre" value={formula.name || formula.product_name} muted={muted} />
          <Field label="Versión" value={`v${formula.version}`} muted={muted} />
          <Field
            label="Rendimiento"
            value={formatQty(formula.yield_qty, formula.yield_unit)}
            muted={muted}
          />
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Estado
            </div>
            <div style={{ marginTop: 4 }}>
              <ProductionStatusChip
                status={formula.status}
                color={getFormulaStatusColor(formula.status)}
              />
            </div>
          </div>
          {normalizeFormulaKind(formula) === FORMULA_KIND_COLOR ? (
            <>
              <Field
                label="Color"
                value={
                  formula.color_code
                    ? `${formula.color_code}${formula.color_name ? ` · ${formula.color_name}` : ''}`
                    : formula.color_name
                }
                muted={muted}
              />
              <Field label="Base" value={formula.base_formula_code} muted={muted} />
            </>
          ) : null}
          <Field
            label="Costos normalizados"
            value={costBasis === 'gl' ? 'Por galón' : 'Por litro'}
            muted={muted}
          />
          <Field label="Actualizada" value={formatDateEs(formula.updated_at)} muted={muted} />
          {formula.product_code ? (
            <Field
              label="Producto (opcional)"
              value={`${formula.product_code}${
                formula.product_name ? ` · ${formula.product_name}` : ''
              }`}
              muted={muted}
            />
          ) : null}
        </div>
        {formula.notes ? (
          <p style={{ margin: '0 0 16px', fontSize: 13, color: muted }}>{formula.notes}</p>
        ) : null}

        {techParams.length ? (
          <div style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: muted,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                marginBottom: 8,
              }}
            >
              Parámetros técnicos
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${border}` }}>
                  {['Parámetro', 'Unidad', 'Ideal', 'Mín', 'Máx'].map((header) => (
                    <th
                      key={header}
                      style={{
                        textAlign: 'left',
                        padding: '6px 8px',
                        fontSize: 11,
                        color: muted,
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {techParams.map((param) => (
                  <tr key={param.key || param.name} style={{ borderBottom: `1px solid ${border}` }}>
                    <td style={{ padding: '6px 8px', fontSize: 13 }}>{param.name || '—'}</td>
                    <td style={{ padding: '6px 8px', fontSize: 13, color: muted }}>{param.unit || '—'}</td>
                    <td style={{ padding: '6px 8px', fontSize: 13 }}>{param.ideal || '—'}</td>
                    <td style={{ padding: '6px 8px', fontSize: 13 }}>{param.min || '—'}</td>
                    <td style={{ padding: '6px 8px', fontSize: 13 }}>{param.max || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${border}` }}>
              {['Código', 'Componente', 'Cantidad', '% m/m', 'Costo unit.', `Costo / ${costBasis}`, 'Merma'].map(
                (header) => (
                  <th
                    key={header}
                    style={{
                      textAlign:
                        header === 'Código' || header === 'Componente' ? 'left' : 'right',
                      padding: '8px 10px',
                      fontSize: 11,
                      color: muted,
                    }}
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const { unitCost, contribution } = formulaLineCostBreakdown(
                line,
                formula,
                materialByCodeMap
              );
              return (
                <tr key={`${line.item_code}-${line.item_name}-${index}`} style={{ borderBottom: `1px solid ${border}` }}>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{line.item_code}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{line.item_name}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {formatQty(line.quantity, line.unit)}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {(massPercents[index] || 0).toFixed(2)}%
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {unitCost > 0 ? formatMoneyClp(unitCost) : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {contribution > 0 ? formatMoneyClp(contribution) : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {Number(line.scrap_pct || 0)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {onEdit ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              type="button"
              onClick={() => onEdit(formula)}
              style={{
                height: 34,
                padding: '0 12px',
                borderRadius: 8,
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Editar fórmula
            </button>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};

export default PaintFormulaDetailModal;
